import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { getOpsHealth } from "../../services/api";
import { useAuthStore } from "../../stores/auth-store";
import type { OpsHealthResponse, TenantHealthCheck, TenantHealthStatus } from "@common/types";
import { categoryHealth, opsFixLink, sortChecksByStatus, versionMismatch } from "./setup-health";

const statusClass: Record<TenantHealthStatus, string> = {
  failing: "text-red-900 bg-red-100 border border-red-300 font-semibold",
  attention: "text-amber-950 bg-amber-50",
  unknown: "text-gray-700 bg-gray-100 border border-dashed border-gray-400",
  ready: "text-green-800 bg-green-50",
  notApplicable: "text-gray-600 bg-gray-50",
};
const statusIcon: Record<TenantHealthStatus, string> = { failing: "✕", attention: "!", unknown: "?", ready: "✓", notApplicable: "–" };
const known: TenantHealthStatus[] = ["failing", "attention", "unknown", "ready", "notApplicable"];
const safeStatus = (value: unknown): TenantHealthStatus => known.includes(value as TenantHealthStatus) ? value as TenantHealthStatus : "unknown";

function formatTime(value: string | undefined, language: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString(language.startsWith("sv") ? "sv-SE" : "en-US");
}

function OpsStatusBadge({ status, t }: { status: TenantHealthStatus; t: TFunction }) {
  return <span data-status={status} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium ${statusClass[status]}`}><span aria-hidden="true">{statusIcon[status]}</span>{t(`ap.health.ops.status.${status}`)}</span>;
}

function OpsRow({ check }: { check: TenantHealthCheck }) {
  const { t, i18n } = useTranslation();
  const status = safeStatus(check.status);
  const link = opsFixLink(check.fixHref, check.meta);
  const success = formatTime(check.lastSuccessAt, i18n.language);
  const failed = formatTime(check.lastFailureAt, i18n.language);
  const version = check.meta?.version;
  const mode = check.meta?.stripeMode;
  const linkClass = "inline-flex min-h-11 items-center text-sm font-medium text-sea-700 underline underline-offset-4";
  return <li className="py-4" data-check-id={check.id}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1 basis-64 break-words">
        <p className="font-medium text-gray-900">{t(`ap.health.ops.checks.${check.id}`, { defaultValue: t("ap.health.ops.unrecognized") })}</p>
        <p className="mt-1"><OpsStatusBadge status={status} t={t} /></p>
        {check.detail && <p className="mt-2 text-sm leading-relaxed text-gray-600">{t(`ap.health.ops.details.${check.detail}`, { defaultValue: t("ap.health.ops.genericDetail") })}</p>}
        {!!check.items?.length && <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-700">{check.items.map(item => <li key={item.id}>{item.name}{!!item.issues?.length && <> ({item.issues.map(issue => t(`ap.health.ops.issues.${issue}`, { defaultValue: "" })).filter(Boolean).join(", ")})</>}</li>)}</ul>}
        {typeof check.meta?.refreshTokenAgeDays === "number" && <p className="mt-1 text-xs text-gray-600">{t("ap.health.ops.refreshAge", { days: check.meta.refreshTokenAgeDays })}</p>}
        {(success || failed) && <p className="mt-1 text-xs text-gray-600">{[success && t("ap.health.ops.lastSuccess", { time: success }), failed && t("ap.health.ops.lastFailure", { time: failed })].filter(Boolean).join(" · ")}</p>}
        {(typeof version === "string" || typeof version === "number") && <p className="mt-1 text-xs text-gray-600">{t("ap.health.ops.version", { value: String(version) })}</p>}
        {typeof mode === "string" && <p className="mt-1 text-xs text-gray-600">{t("ap.health.ops.stripeMode", { value: mode })}</p>}
      </div>
      {link && (link.external
        ? <a className={linkClass} href={link.href} target="_blank" rel="noopener noreferrer">{t("ap.health.ops.openExternal")}</a>
        : <Link className={linkClass} to={link.href}>{t(status === "ready" ? "ap.health.ops.open" : "ap.health.ops.fix")}</Link>)}
    </div>
  </li>;
}

export default function OpsHealth() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(state => state.user);
  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: ["ops-health", user?.tenantId, user?.id],
    enabled: user?.role === "admin",
    queryFn: async () => {
      const response = await getOpsHealth();
      if (!response.success || !response.data) throw new Error("Ops health unavailable");
      return response.data as OpsHealthResponse;
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const cachedMs = data && !isError ? new Date(data.cachedUntil).getTime() : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (Number.isNaN(cachedMs) || cachedMs <= Date.now()) return;
    setNow(Date.now());
    const timer = setTimeout(() => setNow(Date.now()), cachedMs - Date.now() + 50);
    return () => clearTimeout(timer);
  }, [cachedMs]);
  if (user?.role !== "admin") return null;
  const cooling = !Number.isNaN(cachedMs) && now < cachedMs;
  const clock = (value: string | undefined) => { const d = value ? new Date(value) : undefined; return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString(i18n.language.startsWith("sv") ? "sv-SE" : "en-US", { hour: "2-digit", minute: "2-digit" }) : ""; };
  const checkedAt = data && formatTime(data.checkedAt, i18n.language);
  const versionCheck = data?.categories.flatMap(category => category.checks ?? []).find(check => check.id === "backendVersion" && typeof check.meta?.version === "string");
  const mismatch = versionMismatch(versionCheck?.meta?.version, __APP_VERSION__);
  return <section className="space-y-5" aria-labelledby="ops-health-title" aria-busy={isFetching}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="max-w-prose">
        <h2 id="ops-health-title" className="text-xl font-semibold text-gray-900">{t("ap.health.ops.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">{t("ap.health.ops.intro")}</p>
      </div>
      <button type="button" className="btn-secondary min-h-11" disabled={isFetching || cooling} onClick={() => void refetch()}>{t(isFetching ? "ap.health.refreshing" : "ap.health.ops.refresh")}</button>
      {cooling && data && <p className="basis-full text-xs text-gray-600" data-testid="ops-cooldown">{t("ap.health.ops.cooldown", { updated: clock(data.checkedAt), next: clock(data.cachedUntil) })}</p>}
    </div>
    {isPending && !isError && <p role="status" className="text-sm text-gray-600">{t("ap.health.ops.loading")}</p>}
    {isError && <div role="alert" className="rounded-lg bg-amber-50 p-4 text-sm text-amber-950">
      <p className="font-semibold">{t("ap.health.ops.failed")}</p>
      <p className="mt-1">{t("ap.health.ops.failedHint")}</p>
      <button type="button" className="btn-secondary mt-3 min-h-11" disabled={isFetching} onClick={() => void refetch()}>{t("ap.health.ops.retry")}</button>
    </div>}
    {data && !isError && <>
      {checkedAt && <p className="text-sm text-gray-600">{t("ap.health.ops.lastChecked", { time: checkedAt })}</p>}
      {mismatch && <div data-testid="ops-version" className={`rounded-lg p-3 text-sm ${mismatch.differs ? "bg-amber-50 text-amber-950" : "bg-green-50 text-green-800"}`}>
        <p className="font-semibold">{t("ap.health.ops.versionTitle")}: <OpsStatusBadge status={mismatch.differs ? "attention" : "ready"} t={t} /></p>
        <p className="mt-1">{t(mismatch.differs ? "ap.health.ops.versionDiffer" : "ap.health.ops.versionMatch", { backend: mismatch.backend, app: mismatch.app })}</p>
      </div>}
      <div className="grid items-start gap-4 md:grid-cols-2">
        {data.categories.map(category => {
          const checks = sortChecksByStatus((category.checks ?? []).map(check => ({ ...check, status: safeStatus(check.status) })));
          const status = categoryHealth(checks.map(check => check.status));
          return <details key={category.id} open={status === "failing" || status === "attention"} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <summary className="cursor-pointer bg-gray-50 p-4">
              <span className="inline-flex w-[calc(100%-1.5rem)] flex-wrap items-center justify-between gap-3 align-middle">
                <span className="font-semibold text-gray-900">{t(`ap.health.ops.groups.${category.id}`, { defaultValue: t("ap.health.ops.unrecognized") })}</span>
                <OpsStatusBadge status={status} t={t} />
              </span>
            </summary>
            {checks.length ? <ul className="divide-y divide-gray-100 px-4">{checks.map(check => <OpsRow key={check.id} check={check} />)}</ul> : <p className="px-4 py-3 text-sm text-gray-600">{t("ap.health.ops.emptyGroup")}</p>}
          </details>;
        })}
      </div>
    </>}
  </section>;
}
